import React, { useState } from 'react';
import { Sparkles, Loader2, Check, X, ShieldCheck, AlertCircle, RefreshCw } from './Icons';
import { polarService } from '../services/polarService';
import { authService } from '../services/authService';
import { subscriptionService } from '../services/subscriptionService';
import { debugLog } from '../utils/debugLogger';

interface PricingModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const PricingModal: React.FC<PricingModalProps> = ({ onClose, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  const products = polarService.getProducts();

  const handlePurchase = async (productId: string) => {
    debugLog("PRICING", "Checkout initiated", { productId });
    
    setIsLoading(true);
    setError(null);
    setStatusMsg(null);
    try {
      const user = await authService.getCurrentUser();
      
      if (!user) {
        setError("Please log in to continue.");
        setIsLoading(false);
        return;
      }

      await polarService.openCheckout(productId, user.email, user.id);
    } catch (e: any) {
      setError("Failed to open checkout. Please try again.");
      debugLog("PRICING", "Checkout Error", e.message);
      setIsLoading(false);
    }
  };

  const handleRestore = async () => {
    setIsRestoring(true);
    setError(null);
    setStatusMsg(null);
    try {
        const result = await subscriptionService.restorePurchase();
        if (result.success) {
            setStatusMsg("Purchase restored! Refreshing...");
            setTimeout(() => onSuccess(), 1500);
        } else {
            setError(result.message);
        }
    } catch (e: any) {
        setError("Could not restore purchase. Please contact support.");
    } finally {
        setIsRestoring(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-xl" onClick={onClose}></div>
      <div className="relative bg-[#1a1825] border border-white/10 w-full max-w-4xl overflow-hidden rounded-[2.5rem] shadow-2xl flex flex-col md:flex-row animate-slide-up">
        
        <div className="p-8 md:p-12 bg-gradient-to-br from-forge-900 via-spark-900 to-black md:w-2/5 flex flex-col justify-between">
            <div>
                <h2 className="text-3xl font-bold font-serif mb-4 text-white">Pro Access</h2>
                <div className="space-y-4">
                    {[
                      "25 Stories Every Day", 
                      "Premium AI Illustrations", 
                      "Full Voice Narration",
                      "HD PDF Downloads",
                      "No Advertisements"
                    ].map((f, i) => (
                      <div key={i} className="flex items-center gap-3 text-xs text-white/80 font-bold">
                        <Check className="w-4 h-4 text-green-400" /> {f}
                      </div>
                    ))}
                </div>
            </div>
            <div className="mt-12 flex items-center gap-2 text-gray-500 text-[10px] uppercase tracking-widest">
                <ShieldCheck className="w-4 h-4" />
                <span>Secure Polar.sh Payments</span>
            </div>
        </div>

        <div className="p-8 md:p-12 md:w-3/5 bg-[#1a1825] flex flex-col">
            <div className="flex justify-between items-center mb-8">
                <h3 className="text-xl font-bold text-white uppercase tracking-widest">Select Plan</h3>
                <button onClick={onClose} className="text-gray-500 hover:text-white p-2">
                    <X className="w-6 h-6" />
                </button>
            </div>

            {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 text-red-200 text-sm rounded-xl text-center flex items-center gap-2 animate-shake">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                </div>
            )}
            
            {statusMsg && (
                <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 text-green-300 text-sm rounded-xl text-center flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    {statusMsg}
                </div>
            )}

            <div className="flex-1 space-y-6">
                <button 
                  onClick={() => handlePurchase(products.yearly)}
                  disabled={isLoading || !products.yearly}
                  className="w-full relative p-6 rounded-3xl border-2 border-gold-500 bg-gold-500/5 hover:bg-gold-500/10 transition-all text-left flex justify-between items-center group disabled:opacity-50"
                >
                  <div>
                      <div className="font-bold text-lg text-white">Yearly Plan</div>
                      <div className="text-gold-200/50 text-xs mt-1">Best Value - 2 Months Free</div>
                  </div>
                  <div className="text-right">
                      {isLoading ? <Loader2 className="w-6 h-6 animate-spin text-gold-400" /> : (
                        <>
                          <div className="font-bold text-2xl text-white">$99/yr</div>
                          <div className="text-[10px] text-gray-500 uppercase tracking-tighter">Save 20%</div>
                        </>
                      )}
                  </div>
                  <div className="absolute -top-3 right-6 px-3 py-1 bg-gold-500 text-black text-[10px] font-black rounded-full uppercase tracking-widest">Popular</div>
                </button>

                <button 
                  onClick={() => handlePurchase(products.monthly)}
                  disabled={isLoading || !products.monthly}
                  className="w-full p-6 rounded-3xl border border-white/10 hover:bg-white/5 transition-all text-left flex justify-between items-center disabled:opacity-50"
                >
                  <div className="font-bold text-lg text-white">Monthly Plan</div>
                  <div className="text-right">
                      {isLoading ? <Loader2 className="w-6 h-6 animate-spin text-white/20" /> : (
                        <div className="font-bold text-2xl text-white">$11.99/mo</div>
                      )}
                  </div>
                </button>
            </div>

            <div className="mt-8 flex flex-col items-center gap-2">
                <button 
                    onClick={handleRestore}
                    disabled={isRestoring || isLoading}
                    className="flex items-center gap-2 text-xs text-gray-500 hover:text-white transition-colors uppercase tracking-widest font-bold disabled:opacity-50"
                >
                    {isRestoring ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    Restore Purchase
                </button>

                <p className="text-[10px] text-gray-600 text-center leading-relaxed italic">
                    Manage your subscription via the{' '}
                    <a 
                      href={polarService.getCustomerPortalUrl()} 
                      target="_blank" 
                      rel="noreferrer"
                      className="text-gold-500 hover:text-white underline transition-colors"
                    >
                      Customer Portal
                    </a>.
                </p>
            </div>
        </div>
      </div>
    </div>
  );
};

export default PricingModal;